import React from 'react';
import classNames from 'classnames/bind';
import { Combobox, Loader, Pill, PillsInput, useCombobox } from '@mantine/core';

import { Tag } from 'types/tag';

import { useTags } from 'tags/hooks/useTags';

import { Icon } from 'components/Icon/IconDeprecated';

import { TagsTree } from '../TagsTree/TagsTree';
import styles from './TagSelect.module.scss';

const c = classNames.bind(styles);

export type TagSelectProps = {
  className?: string;
  defaultValue?: string[];
  readOnly?: boolean;
  onChange?: (selection: Tag['id'][]) => void;
  onAdd?: (tag: Tag) => void;
  onRemove?: (tagId: string) => void;
  placeholder?: string;
};

export function TagSelect({
  className,
  defaultValue = [],
  readOnly,
  onChange,
  onAdd,
  onRemove,
  placeholder = 'Labels',
}: TagSelectProps) {
  const combobox = useCombobox();

  const [tagIds, setTagIds] = React.useState(defaultValue);

  const { data: selectedTags, isInitialLoading } = useTags({
    params: {
      tag_ids: tagIds,
    },
    enabled: tagIds.length > 0,
  });

  const [search, setSearch] = React.useState('');

  function handleSelectValue(tag: Tag) {
    let updatedTagIds = [...tagIds];

    if (updatedTagIds.includes(tag.id)) {
      updatedTagIds = updatedTagIds.filter((id) => id !== tag.id);
    } else {
      updatedTagIds.push(tag.id);
    }

    setTagIds(updatedTagIds);
    setSearch('');
    onAdd?.(tag);
    onChange?.(updatedTagIds);
  }

  function handleRemoveValue(tagId: Tag['id']) {
    const updatedTagIds = tagIds.filter((id) => id !== tagId);

    setTagIds(updatedTagIds);
    onRemove?.(tagId);
    onChange?.(updatedTagIds);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    combobox.updateSelectedOptionIndex();
    setSearch(event.currentTarget.value);
  }

  return (
    <Combobox store={combobox}>
      <Combobox.DropdownTarget>
        {readOnly ? (
          <div className={c(className)}>
            <Pill.Group gap="xs">
              {(isInitialLoading || !selectedTags) && (
                <span className={c('skeleton-bar', 'list-skeleton')} />
              )}

              {selectedTags?.data.map(({ id, name }) => (
                <Pill
                  key={id}
                  withRemoveButton={!readOnly}
                  variant="primary"
                  size="sm"
                  onRemove={() => handleRemoveValue(id)}
                >
                  {name}
                </Pill>
              ))}
            </Pill.Group>
          </div>
        ) : (
          <PillsInput
            className={c(className)}
            onClick={() => combobox.openDropdown()}
            leftSection={<Icon name="tag" unstyled />}
            rightSection={
              isInitialLoading ? (
                <Loader size="xs" />
              ) : (
                <Icon name="chevron-down" size="small" unstyled />
              )
            }
          >
            <Pill.Group gap="xs">
              {selectedTags?.data.map(({ id, name }) => (
                <Pill
                  variant="primary"
                  size="sm"
                  key={id}
                  withRemoveButton={!readOnly}
                  onRemove={() => handleRemoveValue(id)}
                >
                  {name}
                </Pill>
              ))}

              <Combobox.EventsTarget>
                <PillsInput.Field
                  className={c('input')}
                  onFocus={() => combobox.openDropdown()}
                  value={search}
                  placeholder={placeholder}
                  onChange={handleChange}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        )}
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <TagsTree
            searchTerm={search}
            selection={selectedTags?.data.map(({ id }) => id) ?? defaultValue}
            onAdd={handleSelectValue}
            onRemove={handleRemoveValue}
          />
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
